import { memo, useMemo, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { Button, Form, Input, message, Progress } from "antd";
import {
  ArrowLeftOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { UI_ScreenStyles } from "../UI_ScreenStyles";
import { createBreadcrumbItems, commonBreadcrumbs } from "~/utils/breadcrumbUtils";
import { AuthAPIs } from "~/common/typescript/apis/AuthAPIs";
import Routes from "~/routes/routes";
import { evaluatePasswordStrength } from "./profileUtils";

const ChangePasswordScreen = () => {
  const navigate = useNavigate();
  const [passwordForm] = Form.useForm();
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [newPasswordValue, setNewPasswordValue] = useState<string>("");

  const passwordStrength = useMemo(
    () => evaluatePasswordStrength(newPasswordValue),
    [newPasswordValue],
  );

  const handleChangePassword = async (values: {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    setPasswordLoading(true);
    try {
      await AuthAPIs.remote_Auth_changeMyPassword({
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
      });
      message.success("Đổi mật khẩu thành công");
      passwordForm.resetFields();
      setNewPasswordValue("");
    } catch (error: any) {
      // Global axios interceptor already displays AppError.i18n notifications;
      // only show fallback message if response has no structured AppError
      if (!error?.response?.data?.error?.i18n) {
        const fallbackMsg =
          error?.response?.data?.message || "Đổi mật khẩu thất bại";
        message.error(fallbackMsg);
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <UI_ScreenStyles.UI_Wrapper>
      <UI_ScreenStyles.UI_Breadcrumb
        items={createBreadcrumbItems([
          commonBreadcrumbs.home,
          {
            title: "Thông tin cá nhân",
            href: Routes.PROFILE,
            icon: commonBreadcrumbs.settings.icon,
          },
          { title: "Đổi mật khẩu" },
        ])}
      />

      <UI_ScreenStyles.UI_Content_Wrapper>
        <UI_Container>
          {/* Unified 3-Part Card per src/components/Map/panels/design.md:
              1. Slate Gradient Header (with 34x34 Icon Badge + Back button)
              2. Compact Body (Metadata hint card + Form controls)
              3. Slate FooterActions (Right-aligned Cancel + Primary Submit) */}
          <UI_PanelCard data-testid="profile-change-password-section">
            <UI_PanelHeader data-testid="change-password-top-panel">
              <UI_PanelHeaderLeft>
                <UI_GradientIconBadge>
                  <SafetyCertificateOutlined />
                </UI_GradientIconBadge>
                <UI_HeadingGroup>
                  <UI_MainHeading>Đổi mật khẩu</UI_MainHeading>
                  <UI_SubHeading>
                    Cập nhật mật khẩu định kỳ để bảo vệ tài khoản GeoCloud của bạn
                  </UI_SubHeading>
                </UI_HeadingGroup>
              </UI_PanelHeaderLeft>

              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(Routes.PROFILE)}
                data-testid="change-password-back-btn"
                style={{
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#334155",
                  borderColor: "#cbd5e1",
                }}
              >
                Quay lại hồ sơ
              </Button>
            </UI_PanelHeader>

            <UI_StyledForm
              form={passwordForm}
              layout="vertical"
              onFinish={handleChangePassword}
              validateTrigger={["onBlur", "onSubmit"]}
            >
              <UI_PanelBody>
                <UI_HintBox>
                  Mật khẩu mới cần có tối thiểu <strong>6 ký tự</strong> và không được trùng với mật khẩu hiện tại. Nếu bạn quên mật khẩu đang sử dụng, vui lòng chọn chức năng <strong>"Quên mật khẩu"</strong> tại màn hình đăng nhập.
                </UI_HintBox>

                <Form.Item
                  name="oldPassword"
                  label="Mật khẩu hiện tại"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: "Vui lòng nhập mật khẩu hiện tại",
                    },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: "#94a3b8" }} />}
                    placeholder="Nhập mật khẩu hiện tại"
                    autoComplete="current-password"
                    data-testid="profile-old-password-input"
                  />
                </Form.Item>

                <Form.Item
                  name="newPassword"
                  label="Mật khẩu mới"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: "Vui lòng nhập mật khẩu mới",
                    },
                    { min: 6, message: "Mật khẩu phải có ít nhất 6 ký tự" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const oldPwd = getFieldValue("oldPassword");
                        if (value && oldPwd && value.trim() === oldPwd.trim()) {
                          return Promise.reject(
                            new Error("Mật khẩu mới không được trùng với mật khẩu hiện tại"),
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: "#94a3b8" }} />}
                    placeholder="Nhập mật khẩu mới (tối thiểu 6 ký tự)"
                    autoComplete="new-password"
                    onChange={(e) => setNewPasswordValue(e.target.value)}
                    data-testid="profile-new-password-input"
                  />
                </Form.Item>

                {passwordStrength.level !== "none" && (
                  <UI_PasswordStrengthBox data-testid="profile-password-strength">
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                      <span>Độ mạnh mật khẩu:</span>
                      <strong style={{ color: passwordStrength.color }}>
                        {passwordStrength.label}
                      </strong>
                    </div>
                    <Progress
                      percent={passwordStrength.score}
                      showInfo={false}
                      strokeColor={passwordStrength.color}
                      size="small"
                    />
                  </UI_PasswordStrengthBox>
                )}

                <Form.Item
                  name="confirmPassword"
                  label="Xác nhận mật khẩu mới"
                  dependencies={["newPassword"]}
                  style={{ marginBottom: 4 }}
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: "Vui lòng xác nhận mật khẩu mới",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("newPassword") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error("Mật khẩu xác nhận không khớp"));
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: "#94a3b8" }} />}
                    placeholder="Nhập lại mật khẩu mới"
                    autoComplete="new-password"
                    data-testid="profile-confirm-password-input"
                  />
                </Form.Item>
              </UI_PanelBody>

              <UI_PanelFooter>
                <Button
                  htmlType="button"
                  onClick={() => navigate(Routes.PROFILE)}
                  style={{ borderRadius: 6, fontSize: 13 }}
                >
                  Hủy bỏ
                </Button>
                <UI_PrimarySubmitBtn
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={passwordLoading}
                  data-testid="profile-change-password-btn"
                >
                  Cập nhật mật khẩu
                </UI_PrimarySubmitBtn>
              </UI_PanelFooter>
            </UI_StyledForm>
          </UI_PanelCard>
        </UI_Container>
      </UI_ScreenStyles.UI_Content_Wrapper>
    </UI_ScreenStyles.UI_Wrapper>
  );
};

export default memo(ChangePasswordScreen);

const UI_Container = styled.div`
  max-width: 620px;
  margin: 8px auto 32px auto;
`;

const UI_PanelCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06), 0 1px 3px rgba(15, 23, 42, 0.04);
  overflow: hidden;
`;

const UI_PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const UI_PanelHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UI_GradientIconBadge = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  flex-shrink: 0;
`;

const UI_HeadingGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const UI_MainHeading = styled.h2`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.3;
`;

const UI_SubHeading = styled.p`
  margin: 0;
  font-size: 12px;
  color: #64748b;
  line-height: 1.35;
`;

const UI_StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 16px;
  }

  .ant-form-item-label > label {
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
    height: auto;
  }

  .ant-input,
  .ant-input-affix-wrapper {
    border-radius: 6px;
  }
`;

const UI_PanelBody = styled.div`
  padding: 20px;
  background: #ffffff;
`;

const UI_HintBox = styled.div`
  padding: 10px 14px;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  color: #475569;
  font-size: 12px;
  line-height: 1.5;
  margin-bottom: 18px;
`;

const UI_PasswordStrengthBox = styled.div`
  margin-top: -6px;
  margin-bottom: 14px;
  padding: 8px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 12px;
  color: #475569;
`;

const UI_PanelFooter = styled.div`
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
`;

const UI_PrimarySubmitBtn = styled(Button)`
  border-radius: 6px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  background: #3b82f6 !important;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.25);

  &:hover {
    background: #2563eb !important;
  }
`;
